<script>
import moment from "moment";
import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";

export default {
  name: "ClientAiSideBar",
  components: { SvgIcon },
  props: {
    sessions: { type: Array, default: () => [] },
    currentSession: { type: Object, default: null },
    backRoute: { type: [String, Object], default: null },
    collapsed: { type: Boolean, default: false },
  },
  emits: [
    "newChat",
    "update:currentSession",
    "delete-session",
    "clearHistory",
    "update:collapsed",
  ],
  data() {
    return { searchDialog: false, searchTerm: "" };
  },
  computed: {
    filteredSessions() {
      if (!this.searchTerm) return this.sessions;
      return this.sessions.filter((s) =>
        s.title.toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    },
    groupedSessions() {
      const today = moment().startOf("day");
      const yesterday = moment().subtract(1, "day").startOf("day");
      const groups = { today: [], yesterday: [], pastWeek: [] };
      this.sessions.forEach((session) => {
        const d = moment(session.createdAt);
        if (d.isSame(today, "day")) groups.today.push(session);
        else if (d.isSame(yesterday, "day")) groups.yesterday.push(session);
        else groups.pastWeek.push(session);
      });
      return groups;
    },
    hasAnySessions() {
      const g = this.groupedSessions;
      return g.today.length || g.yesterday.length || g.pastWeek.length;
    },
    filteredGrouped() {
      const today = moment().startOf("day");
      const yesterday = moment().subtract(1, "day").startOf("day");
      const groups = { today: [], yesterday: [], pastWeek: [] };
      this.filteredSessions.forEach((s) => {
        const d = moment(s.createdAt);
        if (d.isSame(today, "day")) groups.today.push(s);
        else if (d.isSame(yesterday, "day")) groups.yesterday.push(s);
        else groups.pastWeek.push(s);
      });
      return groups;
    },
  },
  methods: {
    toggle() {
      this.$emit("update:collapsed", !this.collapsed);
    },
    newChat() {
      this.$emit("newChat");
      this.closeSearchDialog();
    },
    setCurrentSession(session) {
      this.$emit("update:currentSession", session);
      this.closeSearchDialog();
    },
    deleteSession(session) {
      this.$emit("delete-session", session);
    },
    openSearchDialog() {
      this.searchDialog = true;
      this.searchTerm = "";
      this.$nextTick(() => this.$refs.searchInput?.focus());
    },
    closeSearchDialog() {
      this.searchDialog = false;
      this.searchTerm = "";
    },
    goBack() {
      if (this.backRoute) {
        this.$router.push(
          typeof this.backRoute === "string"
            ? { name: this.backRoute }
            : this.backRoute
        );
      }
    },
    formatDate(ts) {
      return moment(ts).format("DD.MM.YYYY");
    },
    async clearHistory() {
      try {
        await this.$confirm(
          this.$t("Вы действительно хотите удалить всю историю?"),
          this.$t("Внимание"),
          {
            confirmButtonText: this.$t("Да"),
            cancelButtonText: this.$t("Отмена"),
            type: "warning",
          }
        );
        this.$emit("clearHistory");
      } catch {
        // cancelled
      }
    },
  },
};
</script>

<template>
  <div class="client-sidebar" :class="{ collapsed }">
    <template v-if="collapsed">
      <div class="collapsed-icons">
        <div class="icon-btn" @click="toggle" :title="$t('Открыть панель')">
          <SvgIcon name="sli-sidebar" class="w-4 h-4 text-muted-foreground" />
        </div>
        <div class="icon-btn" @click="newChat" :title="$t('Новый чат')">
          <SvgIcon name="huge-pen2" class="w-4 h-4 text-muted-foreground" />
        </div>
        <div class="icon-btn" @click="openSearchDialog" :title="$t('Поиск')">
          <SvgIcon name="sli-search" class="w-5 h-5 text-muted-foreground" />
        </div>
      </div>
    </template>

    <template v-else>
      <div class="sidebar-header">
        <div class="header-btn" @click="goBack" :title="$t('Назад')">
          <SvgIcon name="huge-chevron-left" class="w-4 h-4" />
          <span class="header-btn-label">{{ $t("Назад") }}</span>
        </div>
        <div class="header-btn" @click="toggle" :title="$t('Скрыть панель')">
          <SvgIcon name="sli-sidebar" class="w-4 h-4" />
        </div>
      </div>

      <div class="sidebar-menu">
        <!--        <div class="group-title">{{ $t("меню") }}</div>-->
        <div
          class="menu-item"
          :class="{ active: currentSession && !currentSession.messages.length }"
          @click="newChat"
        >
          <SvgIcon name="huge-plus" class="w-4 h-4" />
          <span>{{ $t("Новый чат") }}</span>
        </div>
        <div class="menu-item" @click="openSearchDialog">
          <SvgIcon name="sli-search" class="w-4 h-4" />
          <span>{{ $t("Поиск в чатах") }}</span>
        </div>
      </div>

      <div class="sidebar-chats">
        <div class="group-title-row">
          <div class="group-title">{{ $t("чаты") }}</div>
          <div
            v-if="sessions.length"
            class="clear-btn"
            :title="$t('Очистить историю')"
            @click="clearHistory"
          >
            <SvgIcon name="huge-danger" class="w-3.5 h-3.5" />
          </div>
        </div>
        <template v-if="groupedSessions.today.length">
          <div class="date-label">{{ $t("Сегодня") }}</div>
          <div
            v-for="s in groupedSessions.today"
            :key="s.id"
            class="chat-item"
            :class="{ active: s === currentSession }"
            @click="setCurrentSession(s)"
          >
            <div class="chat-title">{{ s.title }}</div>
            <div class="chat-delete" @click.stop="deleteSession(s)">
              <SvgIcon name="huge-close" class="w-3 h-3" />
            </div>
          </div>
        </template>
        <template v-if="groupedSessions.yesterday.length">
          <div class="date-label">{{ $t("Вчера") }}</div>
          <div
            v-for="s in groupedSessions.yesterday"
            :key="s.id"
            class="chat-item"
            :class="{ active: s === currentSession }"
            @click="setCurrentSession(s)"
          >
            <div class="chat-title">{{ s.title }}</div>
            <div class="chat-delete" @click.stop="deleteSession(s)">
              <SvgIcon name="huge-close" class="w-3 h-3" />
            </div>
          </div>
        </template>
        <template v-if="groupedSessions.pastWeek.length">
          <div class="date-label">{{ $t("За неделю") }}</div>
          <div
            v-for="s in groupedSessions.pastWeek"
            :key="s.id"
            class="chat-item"
            :class="{ active: s === currentSession }"
            @click="setCurrentSession(s)"
          >
            <div class="chat-title">{{ s.title }}</div>
            <div class="chat-delete" @click.stop="deleteSession(s)">
              <SvgIcon name="huge-close" class="w-3 h-3" />
            </div>
          </div>
        </template>
        <div v-if="!hasAnySessions" class="empty-text">
          {{ $t("История чатов пуста") }}
        </div>
      </div>
    </template>

    <Teleport to="body">
      <div
        v-if="searchDialog"
        class="search-overlay"
        @click.self="closeSearchDialog"
      >
        <div class="search-dialog">
          <div class="search-dialog-header">
            <input
              ref="searchInput"
              v-model="searchTerm"
              type="text"
              :placeholder="$t('Поиск в чатах...')"
              class="search-dialog-input"
              @keydown.esc="closeSearchDialog"
            />
            <div class="search-close" @click="closeSearchDialog">
              <SvgIcon name="huge-close" class="w-5 h-5" />
            </div>
          </div>
          <div class="search-dialog-body">
            <div class="search-item" @click="newChat">
              <SvgIcon
                name="huge-plus"
                class="w-4 h-4 search-item-icon"
              /><span>{{ $t("Новый чат") }}</span>
            </div>
            <template v-if="filteredGrouped.today.length">
              <div class="search-group-label">{{ $t("Сегодня") }}</div>
              <div
                v-for="s in filteredGrouped.today"
                :key="s.id"
                class="search-item"
                @click="setCurrentSession(s)"
              >
                <SvgIcon
                  name="huge-message"
                  class="w-5 h-5 search-item-icon"
                /><span>{{ s.title }}</span>
              </div>
            </template>
            <template v-if="filteredGrouped.yesterday.length">
              <div class="search-group-label">{{ $t("Вчера") }}</div>
              <div
                v-for="s in filteredGrouped.yesterday"
                :key="s.id"
                class="search-item"
                @click="setCurrentSession(s)"
              >
                <SvgIcon
                  name="huge-message"
                  class="w-4 h-4 search-item-icon"
                /><span>{{ s.title }}</span>
              </div>
            </template>
            <template v-if="filteredGrouped.pastWeek.length">
              <div class="search-group-label">{{ $t("За неделю") }}</div>
              <div
                v-for="s in filteredGrouped.pastWeek"
                :key="s.id"
                class="search-item"
                @click="setCurrentSession(s)"
              >
                <SvgIcon
                  name="huge-message"
                  class="w-5 h-5 search-item-icon"
                /><span>{{ s.title }}</span>
              </div>
            </template>
            <div
              v-if="searchTerm && !filteredSessions.length"
              class="search-empty"
            >
              {{ $t("Ничего не найдено") }}
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.client-sidebar {
  @apply bg-white border-r border-border;
  width: 280px;
  min-width: 250px;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px;
  display: flex;
  flex-direction: column;
  transition: width 0.25s ease, padding 0.25s ease, min-width 0.25s ease;
  flex-shrink: 0;

  &.collapsed {
    width: 60px;
    min-width: 60px;
    padding: 12px 6px;
    align-items: center;
  }
}

.collapsed-icons {
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: center;
}

.icon-btn {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  cursor: pointer;
  color: rgb(var(--color-gray-700));
  border: 1px solid transparent;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover {
    background-color: rgb(var(--color-white));
    border-color: rgb(var(--color-gray-200));
    color: rgb(var(--color-gray-900));
  }
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.header-btn {
  height: 34px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  color: rgb(var(--color-gray-500));
  border: 1px solid transparent;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover {
    background-color: rgb(var(--card));
    border-color: rgb(var(--color-gray-200));
    color: rgb(var(--color-gray-900));
  }
}

.sidebar-menu {
  margin-bottom: 20px;
}

.group-title {
  text-transform: uppercase;
  font-size: 10px;
  font-weight: 500;
  color: rgb(var(--color-gray-400));
  margin-bottom: 6px;
  letter-spacing: 0.05em;
}

.group-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
  height: 34px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: rgb(var(--color-gray-700));
  border: 1px solid transparent;
  margin-bottom: 3px;

  &:hover {
    background-color: rgb(var(--color-white));
    border-color: rgb(var(--color-gray-200));
    color: rgb(var(--color-gray-900));
  }

  &.active {
    background-color: rgb(var(--color-white));
    border-color: rgb(var(--color-gray-200));
    color: rgb(var(--color-gray-900));
  }
}

.sidebar-chats {
  flex: 1;
  overflow: hidden;
}

.clear-btn {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  color: rgb(var(--color-gray-400));

  &:hover {
    background-color: rgb(var(--color-red-100));
    color: rgb(var(--color-red-500));
  }
}

.date-label {
  font-size: 11px;
  font-weight: 500;
  color: rgb(var(--color-gray-400));
  margin: 12px 0 4px 4px;
}

.chat-item {
  display: flex;
  align-items: center;
  padding: 0 8px;
  height: 34px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  margin-bottom: 3px;

  .chat-title {
    flex: 1;
    font-size: 13px;
    color: rgb(var(--color-gray-600));
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    mask-image: linear-gradient(to right, black 80%, transparent 100%);
    -webkit-mask-image: linear-gradient(to right, black 80%, transparent 100%);
  }

  .chat-delete {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    opacity: 0;
    color: rgb(var(--color-gray-400));
    transition: opacity 0.2s;

    &:hover {
      background-color: rgb(var(--color-red-100));
      color: rgb(var(--color-red-500));
    }
  }

  &:hover {
    background-color: rgb(var(--color-white));
    border-color: rgb(var(--color-gray-200));

    .chat-delete {
      opacity: 1;
    }
  }

  &.active {
    background-color: rgb(var(--color-white));
    border-color: rgb(var(--color-gray-200));

    .chat-title {
      color: rgb(var(--color-gray-900));
    }
  }
}

.empty-text {
  font-size: 12px;
  color: rgb(var(--color-gray-400));
  padding: 8px 4px;
}
.search-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 12vh;
}

.search-dialog {
  width: 100%;
  max-width: 500px;
  @apply bg-white border border-border;
  border-radius: var(--radius);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.search-dialog-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid rgb(var(--color-gray-200));
}

.search-dialog-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 13px;
  background: transparent;
  color: rgb(var(--color-gray-900));

  &::placeholder {
    color: rgb(var(--color-gray-400));
  }
}

.search-close {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  color: rgb(var(--color-gray-400));

  &:hover {
    background-color: rgb(var(--color-gray-100));
    color: rgb(var(--color-gray-900));
  }
}

.search-dialog-body {
  max-height: 400px;
  overflow-y: auto;
  padding: 8px;
}

.search-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
  height: 34px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: rgb(var(--color-gray-700));
  border: 1px solid transparent;
  margin-bottom: 2px;

  &:hover {
    background-color: rgb(var(--color-white));
    border-color: rgb(var(--color-gray-200));
    color: rgb(var(--color-gray-900));
  }
}

.search-item-icon {
  flex-shrink: 0;
  color: rgb(var(--color-gray-400));
}

.search-group-label {
  font-size: 11px;
  font-weight: 500;
  color: rgb(var(--color-gray-400));
  margin: 12px 0 4px 4px;
}

.search-empty {
  padding: 16px 8px;
  text-align: center;
  font-size: 12px;
  color: rgb(var(--color-gray-400));
}
</style>
